import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import logo from '../assets/images/logo.svg';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Add to Firestore newsletter subscriptions collection
      await addDoc(collection(db, 'newsletterSubscriptions'), {
        email: email,
        subscribedAt: serverTimestamp(),
        source: 'website_footer'
      });
      
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      // Still show success message to user for better UX
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  return (
    <footer className="bg-navy-950">
      <div className="container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img
              className="h-12 mb-6"
              src={logo}
              alt="Sultansoft"
            />
            <p className="text-gray-300 text-base leading-relaxed mb-6">
              Building Africa's digital future through innovative technology solutions. 
              From education to finance to telecommunications.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61565854612648" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-crimson-400 transition-colors">
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/105076231" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-crimson-400 transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-display font-bold text-white mb-6">
              Products
            </h3>
            <ul className="space-y-4">
              <li>
                <a href="#products" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  Sultansoft Academy
                </a>
              </li>
              <li>
                <a href="#products" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  Shaka
                </a>
              </li>
              <li>
                <a href="#products" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  Blockchain & Telco
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-display font-bold text-white mb-6">
              Company
            </h3>
            <ul className="space-y-4">
              <li>
                <a href="#about" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="#technology" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  Technology
                </a>
              </li>
              <li>
                <a href="/careers" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  Careers
                </a>
              </li>
              <li>
                <a href="#contact" className="text-gray-300 hover:text-crimson-400 transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-display font-bold text-white mb-6">
              Stay Updated
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              Get the latest updates on our products and African tech innovation.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-xl bg-navy-800 border border-navy-700 text-white placeholder-gray-400 focus:border-crimson-500 focus:ring-crimson-500 transition-colors"
                required
              />
              <button
                type="submit"
                className="w-full btn btn-primary"
              >
                Subscribe
              </button>
            </form>
            {isSubscribed && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-green-400 text-sm mt-2"
              >
                Thank you for subscribing!
              </motion.p>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-navy-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <p>
                &copy; {new Date().getFullYear()} Sultansoft. All rights reserved.
              </p>
              <p className="mt-1">
                D04, 3rd Floor, Karaye Plaza, OPP Ado Bayero Mall, Kano, Nigeria
              </p>
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="/privacy" className="text-gray-400 hover:text-crimson-400 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-400 hover:text-crimson-400 transition-colors">
                Terms of Service
              </a>
              <a href="tel:+2347070893333" className="text-gray-400 hover:text-crimson-400 transition-colors">
                +234 707 089 3333
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;